import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { api, setToken } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setToken(res.data.token);
      navigate("/admin");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to log in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-4">
      <Helmet>
        <title>Staff Login | Kerry's</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="admin-login-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <Card className="rounded-[4px] border-border border-[0.5px]">
          <CardContent className="p-8 sm:p-10">
            <span className="h-12 w-12 rounded-[4px] bg-secondary flex items-center justify-center">
              <Lock className="h-6 w-6 text-primary" />
            </span>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-5">
              Kerry<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;s</span> Studio Admin
            </h1>
            <p className="text-sm text-muted-foreground mt-2">Sign in to manage the blog and timetable.</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="admin-login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  data-testid="admin-login-password-input"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive font-medium" data-testid="login-form-error-text" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={loading} className="w-full rounded-[4px] font-semibold" data-testid="admin-login-submit-button">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
